<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75" @click="close()"/>

                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                                     enter-from="translate-x-full" enter-to="translate-x-0"
                                     leave="transform transition ease-in-out duration-500 sm:duration-700"
                                     leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="w-screen max-w-lg">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"
                                  @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4  bg-gradient-to-b from-pitch-purple to-dark-pitch-purple sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white">
                                                Manage Regions for `{{ country.name}}`
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button"
                                                        class="bg-dark-pitch-purple rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                        @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <p class="text-sm text-indigo-300">
                                                Get started by filling in the information below to add regions to the
                                                country.
                                            </p>
                                        </div>
                                    </div>
                                    <SlideOutErrors   :show="showErrors"/>
                                    <SlideoutFlashMessage ref="create_regions_slideout_flash"/>
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6">
                                            <div class="space-y-6 pt-6 pb-5">
                                                <h2 v-if="updating === true" class="text-xl text-bold">Edit Region</h2>
                                                <h2 v-else class="text-xl text-bold">Add New Region</h2>
                                                <div>
                                                    <label for="region-name"
                                                           class="block text-sm text-gray-900 required">
                                                        Name
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" id="region-name" v-model="form.name"
                                                               class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                               :class="{'border-red-500': form.errors.name}"
                                                        />
                                                    </div>
                                                </div>
                                                <div class="flex justify-end">
                                                    <button :disabled="form.processing || !form.isDirty" type="submit"
                                                            class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-500">
                                                        <span v-if="loading === true">Saving Region..</span>
                                                        <span v-else-if="updating === true">Update Region</span>
                                                        <span v-else>Create Region</span>
                                                    </button>
                                                </div>

                                                <h2 class="text-xl text-bold">Regions</h2>
                                                <div
                                                    class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg relative">

                                                    <table class="min-w-full divide-y divide-gray-200">
                                                        <thead class="bg-gray-50">
                                                        <tr>
                                                            <th scope="col"
                                                                class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                                Name
                                                            </th>
                                                            <th scope="col" class="relative px-6 py-3">
                                                                <span class="sr-only">Edit</span>
                                                            </th>
                                                        </tr>
                                                        </thead>
                                                        <tbody>
                                                        <tr v-if="regions.length > 0" v-for="(region, index) in regions"
                                                            :class="{'bg-gray-100': index % 2}">
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {{ region.name }}
                                                            </td>
                                                            <td class="px-6 w-1/5 py-4 whitespace-nowrap text-right text-sm">

                                                                <a @click="deleteRegion(country, region)"
                                                                   class="text-red-600 hover:text-red-900 float-right hover:cursor-pointer">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none"
                                                                         viewBox="0 0 24 24" class="w-4 h-4"
                                                                         stroke="currentColor">
                                                                        <path stroke-linecap="round"
                                                                              stroke-linejoin="round" stroke-width="2"
                                                                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                                                    </svg>
                                                                </a>
                                                                <PencilAltIcon @click="editRegion(region)" class="text-gray-700 cursor-pointer w-5 h-5"></PencilAltIcon>
                                                            </td>
                                                        </tr>
                                                        <tr v-else>
                                                            <td colspan="2"
                                                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900" :class="{'hidden':loading === true}">
                                                                There are no regions added for this country.
                                                            </td>
                                                        </tr>
                                                        <tr v-if="loading === true && creating === true" :class="{'animate-pulse':loading === true}">
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                <span class="h-4 w-full block bg-slate-200 rounded">&nbsp;</span>
                                                            </td>
                                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                <span class="h-4 w-full block bg-slate-200 rounded">&nbsp;</span>
                                                            </td>
                                                        </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>


                                <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                    <button type="button"
                                            class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                            @click="close()">
                                        Close
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
<script>
import {ref, reactive} from 'vue'
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import {XIcon} from '@heroicons/vue/outline'
import {LinkIcon, PlusSmIcon, QuestionMarkCircleIcon, PencilAltIcon} from '@heroicons/vue/solid'
import {useForm} from '@inertiajs/vue3'
import { router } from '@inertiajs/vue3';

import SlideOutErrors from "@/Components/SlideOutErrors";
import SlideoutFlashMessage from "@/Components/SlideoutFlashMessage";


export default {
    components: {
        SlideoutFlashMessage,
        SlideOutErrors,
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        LinkIcon,
        PlusSmIcon,
        QuestionMarkCircleIcon,
        XIcon,
        PencilAltIcon
    },
    setup(prop, context) {

        const country = ref(null);

        const regions = ref(null);

        const loading = ref(null);
        const creating = ref(null);

        const show = ref(false)
        const showErrors = ref(false)

        const form = useForm({
            id: null,
            name: null,
        })

        const updating = ref(false);

        function submit() {
            this.loading = true;
            this.creating = true;
            if(this.updating === true) {
                this.form.post(route('admin.settings.countries.country.regions.update', this.country.id), {
                    onSuccess: (page) => {
                        this.form.reset();
                        // Make a request for a regions to be fetched.
                        axios.get(route('admin.settings.countries.country.regions.list', this.country.id))
                            .then(response => {
                                this.regions = response.data;
                            }).finally(() => {
                            this.loading = false
                            this.creating = false;
                            this.updating = false;

                        });
                    },
                    onError: (page) => {
                        this.loading = false;
                        this.creating = false;
                        this.showErrors = true;

                    }
                });
            } else {
                this.form.post(route('admin.settings.countries.country.regions.store', this.country.id), {
                    onSuccess: (page) => {
                        this.form.reset();
                        // Make a request for a regions to be fetched.
                        axios.get(route('admin.settings.countries.country.regions.list', this.country.id))
                            .then(response => {
                                this.regions = response.data;
                            }).finally(() => {
                            this.loading = false
                            this.creating = false;
                        });
                    },
                    onError: (page) => {
                        this.loading = false;
                        this.creating = false;
                        this.showErrors = true;

                    }
                });
            }

        }

        function open() {
            this.form.clearErrors();
            this.show = true;
        }


        return {
            show,
            form,
            submit,
            country,
            regions,
            loading,
            creating,
            open,
            showErrors,
            updating
        }
    },
    methods: {
        deleteRegion(country, region) {

            this.$swal.fire({
                title: 'Remove Region',
                html: "You are removing a region. <br/>Are you sure you'd like to do this?",
                showCloseButton: true,
                showCancelButton: true,
                confirmButtonText: 'Yes',
                cancelButtonText: 'No',
                stopKeydownPropagation: true,
                keydownListenerCapture: true
            })
                .then((result) => {
                    if (result.isConfirmed) {
                        // Trigger the loader
                        this.loading = true;
                        // Delete the region
                        this.$inertia.delete(route('admin.settings.countries.country.regions.region.delete', [country, region]), {
                            preserveScroll: true,
                            onSuccess: (page) => {
                                // Make a request for a regions to be fetched.
                                axios.get(route('admin.settings.countries.country.regions.list', this.country.id))
                                    .then(response => {
                                        this.regions = response.data;
                                    }).finally(() => {
                                        this.loading = false
                                });
                            },
                            onError: (page) => {
                                this.loading = false
                                this.showErrors = true;

                            }
                        });
                    }
                });
        },
        editRegion(region) {
          this.form.id = region.id;
          this.form.name = region.name;
          this.updating = true;
        },
        close() {
            this.show = false;
            this.form.reset();
            this.form.clearErrors();
            this.$refs.create_regions_slideout_flash.show = false;
            this.$page.props.slideout_flash.success = null;
            this.$page.props.slideout_flash.error = null;
        }
    }
}
</script>
